"use client";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  Upload,
  Typography,
  Space,
  Row,
  Col,
  message,
  Affix,
} from "antd";
import {
  BookOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  LineChartOutlined,
  RocketOutlined,
  ApartmentOutlined,
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusCircleFilled,
} from "@ant-design/icons";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";
import ImgCrop from "antd-img-crop";
import { ShouldRender } from "../shared/basic/ShouldRender";
import UploadLive from "../../assets/Icons/UploadLive";
import {
  createLive,
  editLive,
  getAllAuthors,
  getGlobalCategories,
  getLiveDetails,
} from "../../services/live";
import moment from "moment";
import extractErrorMessage from "../shared/basic/formateError";
import { notify } from "../shared/basic/notify";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { motion, useInView, useAnimation } from "framer-motion";
import { getAdminRole } from "../../utils/lib/auth";
import { UserData } from "../../utils/interfaces/user";
import { user } from "../../atoms/toggleTheme/user";
import { useRecoilValue } from "recoil";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const DESCRIPTIONS = [
  {
    key: "desc1",
    placeholder: "Learn Advance UX/UI Design",
    icon: <BookOutlined />,
  },
  {
    key: "desc2",
    placeholder: "120 Minutes Live Class",
    icon: <ClockCircleOutlined />,
  },
  {
    key: "desc3",
    placeholder: "Watch Anytime, Anywhere",
    icon: <EnvironmentOutlined />,
  },
  {
    key: "desc4",
    placeholder: "Beginners to Intermediate Level",
    icon: <LineChartOutlined />,
  },
  {
    key: "desc5",
    placeholder: "Interactive & Easy-to-Follow",
    icon: <RocketOutlined />,
  },
  {
    key: "desc6",
    placeholder: "Industry Level Knowledge",
    icon: <ApartmentOutlined />,
  },
];

const INPUT_STYLE =
  "border text-base focus-within:bg-bgColor px-4 placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray py-2 text-white bg-bgColor";

const ScrollReveal = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.2,
  });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.6, delay: delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const SectionHeader = React.memo(({ title }: { title: string }) => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.2,
  });

  useEffect(() => {
    if (isInView) {
      controls.start({ opacity: 1, y: 0 });
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={controls}
      transition={{ duration: 0.5 }}
      className="flex justify-center items-center gap-x-5 py-2 mb-6"
    >
      <div className="flex whitespace-nowrap text-[#C7C6CF] text-xs">
        {title}
      </div>
      <div className="bg-[#3C3B3E] h-[1px] w-full" />
    </motion.div>
  );
});

SectionHeader.displayName = "SectionHeader";

const CreateLiveForm = () => {
  const [form] = Form.useForm();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<any>({ modules: [] });
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [authors, setAuthors] = useState<
    { name: string; zl_uid: string; email: string }[]
  >([]);
  const [categories, setCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const isAdmin = getAdminRole();
  const userData = useRecoilValue<UserData | undefined>(user);

  const router = useNavigate();
  const { id } = useParams();

  const onRemove = useCallback(() => {
    setFormData((prev: any) => ({ ...prev, thumbnail: null }));
    setFileList([]);
    return true;
  }, []);

  const handleFormChange = useCallback((_: any, allValues: any) => {
    setFormData((prev: any) => ({
      ...prev,
      ...allValues,
      thumbnail:
        prev.thumbnail instanceof File ? prev.thumbnail : prev.thumbnail,
    }));
  }, []);

  const formatFormDataForSubmission = useCallback(() => {
    const whyChooseThis = DESCRIPTIONS.map(({ key }) => ({
      key,
      value: formData[key],
    })).filter(({ value }) => value && value.trim() !== "");

    const filteredFormData = { ...formData };
    DESCRIPTIONS.forEach(({ key }) => delete filteredFormData[key]);

    return {
      title: formData.title || "",
      description: formData.description || "",
      date: formData.date?.format("YYYY-MM-DD") as string,
      start_time: moment(formData.startTime.toDate()).format("HH:mm:ss"),
      end_time: moment(formData.endTime.toDate()).format("HH:mm:ss"),
      extra: whyChooseThis,
      author_name:
        isAdmin === "false"
          ? (userData?.name as string)
          : (authors?.find((a) => a.zl_uid === formData.author_zl_uid)
              ?.name as string),
      author_zl_uid:
        isAdmin === "false"
          ? (userData?.zl_uid as string)
          : formData.author_zl_uid || "",
      category: categories?.find((c) => c.value === formData.category_id)
        ?.label as string,
      category_id: (formData.category_id || "") as string,
      iframe: (formData.iFrameLink || "") as string,
      status: formData.status || "approved",

      modules:
        formData?.modules?.map((module: any) => ({
          title: module.title || "",
          description: module.description || "",
        })) || [],
      ...(typeof formData.thumbnail !== "undefined" &&
        formData.thumbnail instanceof File && {
          thumbnail: formData.thumbnail,
        }),
    };
  }, [formData, authors, categories]);

  const handleSubmit = useCallback(
    async (type?: string) => {
      try {
        setLoading(true);
        if (type !== "pending") {
          await form.validateFields();
        }

        const formattedValues = formatFormDataForSubmission();
        if (type) {
          formattedValues.status = type;
        }

        if (id) {
          await editLive(formattedValues, id);
        } else {
          await createLive(formattedValues);
        }

        router("/live");
        notify(`Live ${id ? "edited" : "created"} successfully!`, "success");
      } catch (error: any) {
        if (error?.errorFields) {
          notify("Please fill out all required fields correctly.", "error");
        } else {
          const message = extractErrorMessage(error);
          notify(message, "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [form, formatFormDataForSubmission, id, router]
  );

  const fetchCategories = useCallback(async () => {
    try {
      const response = await getGlobalCategories();
      setCategories(
        response?.data.map((category: any) => ({
          label: category?.name,
          value: category?.id,
        }))
      );
    } catch (error: any) {
      const message = extractErrorMessage(error);
      notify(message, "error");
    }
  }, []);

  const fetchAuthors = useCallback(async () => {
    try {
      const authors = await getAllAuthors();
      setAuthors(
        authors?.data?.data
          ?.filter((author: any) => author?.zl_uid && author?.email)
          .map((author: any) => ({
            name: author?.name,
            email: author?.email,
            zl_uid: author?.zl_uid,
          }))
      );
    } catch (error) {
      console.error("API call error:", error);
    }
  }, []);

  const fetchLiveData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await getLiveDetails(id);
      const liveData = response?.data;

      const extraFields: Record<string, string> = {};
      if (Array.isArray(liveData.extra)) {
        liveData.extra.forEach((item: any) => {
          try {
            const parsed = typeof item === "string" ? JSON.parse(item) : item;
            extraFields[parsed.key] = parsed.value;
          } catch (e) {
            console.error("Error parsing extra item:", e);
          }
        });
      }

      const parsedModules = Array.isArray(liveData.modules)
        ? liveData.modules.map((module: any) => {
            try {
              return typeof module === "string" ? JSON.parse(module) : module;
            } catch (e) {
              console.error("Error parsing module:", e);
              return module;
            }
          })
        : [];

      const formValues = {
        ...liveData,
        ...extraFields,
        date: liveData.date ? dayjs(liveData.date) : undefined,
        startTime: liveData.start_time
          ? dayjs(liveData.start_time, "HH:mm:ss")
          : undefined,
        endTime: liveData.end_time
          ? dayjs(liveData.end_time, "HH:mm:ss")
          : undefined,
        modules: parsedModules,
        category: liveData.category,
        author_zl_uid: liveData.author_zl_uid,
        authorName: liveData.author_name,
        iFrameLink: liveData.iframe,
      };

      setFormData(formValues);
      form.setFieldsValue(formValues);

      if (liveData?.thumbnail) {
        setFileList([
          {
            uid: "-1",
            name: "thumbnail.png",
            status: "done",
            url: liveData.thumbnail,
          },
        ]);
        setFormData((prev: any) => ({
          ...prev,
          thumbnail: {
            uid: "-1",
            name: liveData.thumbnail,
            status: "done",
            url: liveData.thumbnail,
          },
        }));
      }
    } catch (error) {
      console.error("API call error:", error);
      notify("Failed to load live data", "error");
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchCategories(), fetchAuthors()]);
      await fetchLiveData();
    };

    fetchData();
  }, [fetchCategories, fetchAuthors, fetchLiveData]);

  const onChange = (info: UploadChangeParam<UploadFile>) => {
    const newFileList = info.fileList;

    if (newFileList.length === 0) {
      setFormData((prev: any) => ({ ...prev, thumbnail: null }));
      setFileList([]);
      return;
    }

    const file = newFileList[0]?.originFileObj;

    if (file) {
      setFormData((prev: any) => ({
        ...prev,
        thumbnail: file,
      }));
      setFileList(newFileList);
    }
  };
  const onPreview = useCallback(async (file: UploadFile) => {
    let src = file.url as string;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as Blob);
        reader.onload = () => resolve(reader.result as string);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  }, []);

  const handleAuthorChange = useCallback(
    (value: string) => {
      const selected = authors?.find((a) => a.zl_uid === value);
      form.setFieldsValue({
        authorName: selected?.name,
        zl_uid: selected?.zl_uid,
      });
      setFormData((prev: any) => ({
        ...prev,
        authorName: selected?.name,
        zl_uid: selected?.zl_uid,
        author_zl_uid: value,
        thumbnail:
          prev.thumbnail instanceof File ? prev.thumbnail : prev.thumbnail,
      }));
    },
    [authors, form]
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      const selected = categories?.find((c) => c.value === value);
      form.setFieldsValue({
        category: selected?.label,
        category_id: selected?.value,
      });
      setFormData((prev: any) => ({
        ...prev,
        category: selected?.label,
        category_id: selected?.value,
        thumbnail:
          prev.thumbnail instanceof File ? prev.thumbnail : prev.thumbnail,
      }));
    },
    [categories, form]
  );

  const renderDescriptionItems = useMemo(
    () => (
      <Row gutter={[16, 16]} justify="center">
        {DESCRIPTIONS.map(({ key, icon, placeholder }, index) => {
          return (
            <Col xs={24} sm={12} md={8} key={key}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center justify-start bg-[#FFFFFF14] rounded-xl py-5 px-12"
              >
                <span className="text-[#FF6B00] mr-2">{icon}</span>
                <span className="text-white">
                  {formData[key] ||
                    form.getFieldValue(key) ||
                    placeholder ||
                    "Description"}
                </span>
              </motion.div>
            </Col>
          );
        })}
      </Row>
    ),
    [form, formData]
  );

  const renderDescriptionInputs = useMemo(
    () => (
      <Row gutter={[16, 16]}>
        {DESCRIPTIONS.map(({ key, placeholder, icon }) => (
          <Col xs={24} sm={12} key={key}>
            <Form.Item
              name={key}
              rules={[
                {
                  message: "Please enter a description",
                },
              ]}
            >
              <Input
                prefix={
                  <div className="flex gap-x-2 justify-center items-center">
                    <span className="text-[#FFFFFF80]">{icon}</span>
                    <span className="px-1 mb-1 text-primary">|</span>
                  </div>
                }
                placeholder={placeholder}
                className={INPUT_STYLE}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    ),
    []
  );

  return (
    <div className="flex justify-center">
      <div className="max-w-[80%] pt-16">
        <Affix offsetTop={0} style={{ zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-between items-center mb-5 bg-bgColor py-4"
          >
            <h1 className="m-0 text-2xl font-semibold text-white">
              {id ? "Edit" : "Create New Live"} Live
            </h1>
            <Space>
              <Button
                onClick={() => router("/live")}
                className="bg-transparent border-[#333] text-white"
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                onClick={() => handleSubmit("pending")}
                className="bg-transparent border-[#333] text-white"
                loading={loading}
              >
                Save as Draft
              </Button>

              <Button
                type="primary"
                onClick={() => handleSubmit()}
                loading={loading}
              >
                {id ? "Edit" : "Save"} Live
              </Button>
            </Space>
          </motion.div>
        </Affix>

        <Form
          form={form}
          layout="vertical"
          onFinish={() => message.success("Live class created successfully!")}
          onValuesChange={handleFormChange}
          requiredMark={false}
          className="text-white"
          initialValues={{
            modules: [],
          }}
        >
          <SectionHeader title="GENERAL INFORMATION" />

          <ScrollReveal>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="title"
                  label={<span className="text-white">Title</span>}
                  rules={[{ required: true, message: "Please enter a title" }]}
                >
                  <Input placeholder="Enter title" className={INPUT_STYLE} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="description"
                  label={<span className="text-white">Description</span>}
                  rules={[
                    { required: true, message: "Please enter a description" },
                  ]}
                >
                  <TextArea
                    placeholder="Enter live description..."
                    rows={4}
                    className={INPUT_STYLE}
                  />
                </Form.Item>
              </Col>
              <ShouldRender check={isAdmin !== "false"}>
                <Col span={12}>
                  <Form.Item
                    name="author_zl_uid"
                    label={<span className="text-white">Author Name</span>}
                    rules={[
                      { required: true, message: "Please select an author" },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Author"
                      style={{ height: 42 }}
                      dropdownStyle={{ backgroundColor: "#1f1f1f" }}
                      className="antdSelect antdSelectDropdown"
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={handleAuthorChange}
                    >
                      {authors?.map((author) => (
                        <Option
                          key={author.zl_uid}
                          value={author.zl_uid}
                          label={author.name}
                        >
                          {author.name} ({author.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </ShouldRender>

              <Col span={12}>
                <Form.Item
                  name="category"
                  label={<span className="text-white">Category</span>}
                  rules={[
                    { required: true, message: "Please select a category" },
                  ]}
                >
                  <Select
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    showSearch
                    placeholder="Enter category"
                    onChange={handleCategoryChange}
                    style={{ height: 42 }}
                    className="antdSelect antdSelectDropdown"
                    dropdownStyle={{ backgroundColor: "#1f1f1f" }}
                  >
                    {categories?.map((category) => (
                      <Option
                        key={category.value}
                        value={category.value}
                        label={category.label}
                      >
                        {category.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="date"
                  label={<span className="text-white">Date</span>}
                  rules={[{ required: true, message: "Please select a date" }]}
                >
                  <DatePicker
                    className="w-full py-2 rounded-[12px] border-bgDarkGray bg-transparent text-base"
                    placeholder="Select Date"
                  />
                </Form.Item>
              </Col>

              <Col span={isAdmin !== "false" ? 6 : 12}>
                <Form.Item
                  name="startTime"
                  label={<span className="text-white">Start Time</span>}
                  rules={[
                    { required: true, message: "Please select start time" },
                  ]}
                >
                  <TimePicker
                    className="w-full py-2 rounded-[12px] border-bgDarkGray bg-transparent text-base"
                    placeholder="Start Time"
                    format="HH:mm"
                  />
                </Form.Item>
              </Col>

              <Col span={isAdmin !== "false" ? 6 : 12}>
                <Form.Item
                  name="endTime"
                  label={<span className="text-white">End Time</span>}
                  rules={[
                    { required: true, message: "Please select end time" },
                  ]}
                >
                  <TimePicker
                    className="w-full py-2 rounded-[12px] border-bgDarkGray bg-transparent text-base"
                    placeholder="End Time"
                    format="HH:mm"
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="iFrameLink"
                  label={<span className="text-white">IFrame Link</span>}
                  rules={[
                    { required: true, message: "Please enter iframe link" },
                  ]}
                >
                  <Input
                    placeholder="Enter iframe link"
                    className={INPUT_STYLE}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="thumbnail"
                  rules={[
                    { required: true, message: "Please provide thumbnail" },
                  ]}
                >
                  <div className="flex justify-between gap-x-4">
                    <div className="w-full flex justify-center items-start flex-col">
                      <div className="text-white text-base block mb-1">
                        Upload Thumbnail
                      </div>
                      <span className="text-[#FFFFFF75]">
                        Upload a image in png format. (Size: 1440X720)
                      </span>
                    </div>
                    <div className="w-full">
                      <ImgCrop rotationSlider aspect={2 / 1}>
                        <Upload
                          listType="picture-card"
                          fileList={fileList}
                          onChange={onChange}
                          onPreview={onPreview}
                          onRemove={onRemove}
                          maxCount={1}
                          accept="image/*"
                          className="custom-upload"
                        >
                          {fileList.length === 0 && (
                            <div className="flex gap-x-2 justify-center items-center text-[#FFFFFF75]">
                              <UploadLive />
                              Drag files to attach or click here
                            </div>
                          )}
                        </Upload>
                      </ImgCrop>
                    </div>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </ScrollReveal>

          <SectionHeader title="WHY CHOOSE LIVE CLASS" />

          <ScrollReveal delay={0.2}>
            <div className="mb-8">
              <div className="border-bgDarkGray border px-16 py-10 rounded-2xl">
                <Title level={4} className="text-white text-center">
                  Why Choose This Live Class?
                </Title>
                {/* <Text className="block text-[#999] text-center text-xs mb-6">
                  Master the latest digital marketing strategies and boost your
                  career with this course.
                </Text> */}

                {renderDescriptionItems}
              </div>

              <div className="mt-10">
                <Text className="text-white text-base block mb-4">
                  Enter Description
                </Text>

                {renderDescriptionInputs}
              </div>
            </div>
          </ScrollReveal>

          <SectionHeader title="WHAT YOU WILL LEARN" />

          <ScrollReveal delay={0.3}>
            <Form.List name="modules">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => {
                      const module =
                        form.getFieldValue(["modules", index]) || {};
                      const isEditing = editingIndex === index;

                      return (
                        <motion.div
                          key={field.key}
                          className="mb-4"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{
                            once: true,
                            amount: 0.2,
                          }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <div className="border border-[#282829] rounded-[12px] ">
                            <div className="flex bg-[#151515] justify-between items-center px-4 py-3">
                              <span className="">Module {index + 1}</span>
                              <Space>
                                {isEditing ? (
                                  <>
                                    <Button
                                      type="primary"
                                      icon={<SaveOutlined />}
                                      size="middle"
                                      onClick={() => setEditingIndex(null)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="middle"
                                      className="bg-[#FFFFFF14] border-none"
                                      onClick={() => setEditingIndex(null)}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      icon={<EditOutlined />}
                                      size="middle"
                                      onClick={() => setEditingIndex(index)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      danger
                                      icon={<DeleteOutlined />}
                                      size="middle"
                                      onClick={() => {
                                        remove(index);
                                        if (editingIndex === index)
                                          setEditingIndex(null);
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </Space>
                            </div>
                            <ShouldRender check={isEditing}>
                              <Form.Item
                                {...field}
                                name={[field.name, "title"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter a title",
                                  },
                                ]}
                                className=" px-4 pt-3"
                              >
                                <Input
                                  placeholder="Module Title"
                                  className={INPUT_STYLE}
                                />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                name={[field.name, "description"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter a description",
                                  },
                                ]}
                                className="px-4 pb-3"
                              >
                                <TextArea
                                  rows={3}
                                  placeholder="Module Description"
                                  className={INPUT_STYLE}
                                />
                              </Form.Item>
                            </ShouldRender>
                            <ShouldRender check={!isEditing}>
                              <Text
                                strong
                                className="text-white block mb-2 px-4 py-3 border-b border-[#282829]"
                              >
                                {module.title || "Untitled Module"}
                              </Text>
                              <div className="text-[#999] px-4 py-3">
                                {module.description || "No description"}
                              </div>
                            </ShouldRender>
                          </div>
                        </motion.div>
                      );
                    })}

                    <motion.div
                      className="flex w-min mt-3"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{
                        once: true,
                        amount: 0.2,
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      <Button
                        icon={<PlusCircleFilled />}
                        className="bg-[#FFFFFF14] border-none rounded-[12px] h-[41px] px-5 text-base py-2 text-white"
                        onClick={() => {
                          add({ title: "", description: "" });
                        }}
                      >
                        Add Module
                      </Button>
                    </motion.div>
                  </>
                );
              }}
            </Form.List>
          </ScrollReveal>
        </Form>
      </div>
    </div>
  );
};

export default CreateLiveForm;
